// Import necessary dependencies and components
import React, { useMemo, useState, useEffect } from "react";
import Container from "react-bootstrap/Container";
import {
  Row,
  Col,
  Badge,
  Nav,
  Form,
} from "react-bootstrap";
import ClickBack from "../../components/buttons/ClickBack";
import DynamicTable from "../../components/DynamicTable";
import { fetchUserData, updateUserStatus } from "./dataService";
import IconTrash from "../../images/trash.png";
import IconEdit from "../../images/edit.png";
import IconShow from "../../images/fi_eye.png";
import ConfirmationModal from "../../components/modal/ConfirmationModal";
import OkModal from "../../components/modal/OkModal";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import SearchBar from "../../components/SearchBar";
// Define the functional component
function ListScreen(props) {
  // State to hold user data
  const [userData, setUserData] = useState(null);
  const [userRole, setUserRole] = useState(1); // Initial user role
  const [title, setTitle] = useState("Particuliers"); // Initial title
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [showOkModal, setShowOkModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [nbr, setNbr] = useState(1);
  const [actionType, setActionType] = useState("");
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [roleConnected, setRoleConnected] = useState(null);

  // Filtres de l'onglet Entreprise. "" = Tous, "1" = Oui, "0" = Non — "0" est une
  // valeur significative, à ne pas confondre avec l'absence de filtre.
  const FILTRES_VIDES = {
    paiement_en_compte: "",
    exclu_relance_facture: "",
    is_test: "",
    beta_new_order_form: "",
  };
  const [filtres, setFiltres] = useState(FILTRES_VIDES);

  // Function to fetch user data based on role
  const fetchData = async (role) => {
    try {
      setLoading(true);
      const data = await fetchUserData(role, nbr, searchTerm, filtres);
      setUserData(data.data);
    } catch (error) {
      console.error("Error fetching user data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Function to handle user role change
  const handleUserRoleChange = (role) => {
    // Update the user role and fetch data for the new role
    setUserRole(role);

    // Réinitialiser les filtres : ils ne sont affichés que sur l'onglet Entreprise,
    // mais l'état survit au changement d'onglet. Sans ça, un filtre posé ici
    // resterait envoyé à l'API sur Particuliers — invisible, et la liste
    // apparaîtrait tronquée sans explication.
    setFiltres(FILTRES_VIDES);
    setNbr(1);

    // Pas d'appel explicite à fetchData ici : le useEffect se déclenche déjà sur
    // userRole. L'appeler quand même relancerait une requête avec les filtres
    // d'avant la réinitialisation, qui pourrait revenir après la bonne.

    // Update the title based on the user role using a switch statement
    switch (role) {
      case 1:
        setTitle("Particuliers");
        break;
      case 3:
        setTitle("Livreur");
        break;
      case 2:
        setTitle("Entreprise ");
        break;
      case 5:
        setTitle("Admin");
        break;
      default:
        setTitle("Particuliers");
    }
  };

  const updateUserStatusInactive = async () => {
    try {
      const response = await updateUserStatus(selectedUserId);
      if (response.status === 200) {
        setShowOkModal(true);
        fetchData(userRole); // Refresh data after deletion
      } else {
        throw new Error(
          `Échec lors de la suppression du l'utilisateur: ${response.status}`
        );
      }
    } catch (error) {
      console.error("Erreur lors de la suppression du l'utilisateur :", error);
    } finally {
      setShowConfirmationModal(false);
    }
  };

  //to confirm modal
  const handleCloseConfirmationModal = () => {
    setShowConfirmationModal(false);
  };
  //to close modal
  const handleOkModalClose = () => {
    setShowOkModal(false);
  };
  //to confirm delete modal
  const handleDeleteClick = (userId) => {
    setSelectedUserId(userId);
    setActionType("delete");
    setShowConfirmationModal(true);
  };
    // Update search term and fetch data
    const handleSearch = (term,nbr,role) => {
      setSearchTerm(term);
      setNbr(1); // Reset to first page
      //fetchData(role, nbr, searchTerm); // Trigger fetch with updated search term
    };

  // Retour en première page à chaque changement de filtre, comme pour la recherche :
  // filtrer depuis la page 4 afficherait sinon une page vide.
  const handleFiltreChange = (champ, valeur) => {
    setFiltres((precedents) => ({ ...precedents, [champ]: valeur }));
    setNbr(1);
  };
  // useEffect to automatically load data when the component mounts
  useEffect(() => {
    fetchData(userRole, nbr);
    const roleStorage = Number(
      JSON.parse(localStorage.getItem("userData") || "{}")?.role
    );
    setRoleConnected(roleStorage);
  }, [userRole, nbr, searchTerm, roleConnected, filtres]); // Empty dependency array means it will run once when the component mounts

  // Memoized columns for the DynamicTable component
  const columns = useMemo(() => {
    return [
      { Header: "ID", accessor: "id" },
      { Header: "Prénom", accessor: "prenom" },
      { Header: "Nom", accessor: "nom" },
      userRole === 3
        ? { Header: "Téléphone", accessor: "user_profil.telephone" }
        : null,
      { Header: "E-mail", accessor: "email" },
      {
        Header: "Etat",
        accessor: "status",
        Cell: ({ value }) => (
          <Badge className={value === 1 ? "bg-success" : "bg-danger"}>
            {value === 1 ? "Active" : "Inactive"}
          </Badge>
        ),
      },
      {
        Header: "Paiement",
        accessor: "paiement_en_compte",
        Cell: ({ value }) => (
          <Badge className={value ? "bg-info" : "bg-secondary"}>
            {value ? "En compte" : "Comptant"}
          </Badge>
        ),
      },
      {
        Header: "Date de création",
        accessor: "created_at",
        Cell: ({ value }) =>
          format(new Date(value), "dd-MM-yyyy", { locale: fr }),
      },
      {
        Header: "Actions",
        Cell: ({ value, row }) => (
          <div>
            <a href={`user/show/${row.original.id}`} className="p-2">
              <img src={IconShow} width="20px" alt="Voir" />
            </a>
            <a href={`user/edit/${row.original.id}`} className="p-2">
              <img src={IconEdit} width="20px" alt="Modifier" />
            </a>
            <a
              href="#"
              className="p-2"
              onClick={() => handleDeleteClick(row.original.id)}
            >
              <img src={IconTrash} width="20px" alt="Supprimer" />
            </a>
          </div>
        ),
      },
    ].filter(Boolean); // Filter out any falsey values
  }, [nbr, userRole]); // Add dependencies if needed

  // Render loading message while data is being fetched
  if (!userData) {
    return (
      <Container>
        <p>Chargement...</p>
      </Container>
    );
  }

  // Render the component
  return (
    <Container>
      <div className="userList">
        {/* Breadcrumb navigation */}
        <p className="h6">Accueil / Utilisateurs / {title}</p>

        {/* Header and filters */}
        <Row className="pt-4 pb-2">
          <Col style={{ display: "flex", alignItems: "center" }}>
            <ClickBack />
            {/* Display dynamic title */}
            <h1 className="p-1">{title}</h1>
          </Col>
          <Col
            xs
            lg="2"
            className="d-flex justify-content-end align-items-center"
            style={{ alignContent: "right" }}
          >
            {/* add button */}
            <Col
              className="d-flex justify-content-end align-items-center"
              style={{ alignContent: "left" }}
            >
              <a className="btn btn-primary" href="/user/add">
                Ajouter Utilisateur
              </a>
            </Col>
          </Col>
        </Row>

        {/* User type tabs */}
        <Nav variant="tabs" className="mb-3">
          <Nav.Item>
            <Nav.Link
              active={userRole === 1}
              onClick={() => handleUserRoleChange(1)}
              style={{ cursor: 'pointer' }}
            >
              Particuliers
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link
              active={userRole === 3}
              onClick={() => handleUserRoleChange(3)}
              style={{ cursor: 'pointer' }}
            >
              Livreur
            </Nav.Link>
          </Nav.Item>
          <Nav.Item>
            <Nav.Link
              active={userRole === 2}
              onClick={() => handleUserRoleChange(2)}
              style={{ cursor: 'pointer' }}
            >
              Entreprise
            </Nav.Link>
          </Nav.Item>
          {roleConnected === 5 && (
            <Nav.Item>
              <Nav.Link
                active={userRole === 5}
                onClick={() => handleUserRoleChange(5)}
                style={{ cursor: 'pointer' }}
              >
                Admin
              </Nav.Link>
            </Nav.Item>
          )}
        </Nav>
          <SearchBar searchTerm={searchTerm} handleSearch={handleSearch} />

        {/* Filtres propres aux comptes entreprise : sans objet pour un particulier,
            un livreur ou un admin, donc masqués sur les autres onglets. */}
        {userRole === 2 && (
          <Row className="mb-3 g-2">
            <Col xs={12} md={3}>
              <Form.Label className="small text-muted mb-1">En compte</Form.Label>
              <Form.Select
                size="sm"
                value={filtres.paiement_en_compte}
                onChange={(e) => handleFiltreChange("paiement_en_compte", e.target.value)}
              >
                <option value="">Tous</option>
                <option value="1">En compte</option>
                <option value="0">Hors compte</option>
              </Form.Select>
            </Col>
            <Col xs={12} md={3}>
              <Form.Label className="small text-muted mb-1">Relances facture</Form.Label>
              <Form.Select
                size="sm"
                value={filtres.exclu_relance_facture}
                onChange={(e) => handleFiltreChange("exclu_relance_facture", e.target.value)}
              >
                <option value="">Toutes</option>
                <option value="1">Exclu</option>
                <option value="0">Actives</option>
              </Form.Select>
            </Col>
            <Col xs={12} md={3}>
              <Form.Label className="small text-muted mb-1">Compte de test</Form.Label>
              <Form.Select
                size="sm"
                value={filtres.is_test}
                onChange={(e) => handleFiltreChange("is_test", e.target.value)}
              >
                <option value="">Tous</option>
                <option value="1">Test uniquement</option>
                <option value="0">Comptes réels</option>
              </Form.Select>
            </Col>
            <Col xs={12} md={3}>
              <Form.Label className="small text-muted mb-1">Fonctionnalités en test</Form.Label>
              <Form.Select
                size="sm"
                value={filtres.beta_new_order_form}
                onChange={(e) => handleFiltreChange("beta_new_order_form", e.target.value)}
              >
                <option value="">Tous</option>
                <option value="1">Activées</option>
                <option value="0">Non activées</option>
              </Form.Select>
            </Col>
          </Row>
        )}

        {/* Render the DynamicTable component */}
        {loading ? (
          // Display loader while data is being fetched
          <p className="lead">Chargement...</p>
        ) : (
          <DynamicTable
            columns={columns}
            // setSearchTerm={setSearchTerm}
            data={userData?.data}
            link={userData?.links}
            // next_page_url={userData?.next_page_url}
            // prev_page_url={userData?.prev_page_url}
            last_page={userData?.last_page}
            setNbr={setNbr}
            nbr={nbr}
            total={userData?.total}
            name="user"
          />
        )}
      </div>
      <ConfirmationModal
        show={showConfirmationModal}
        onClose={handleCloseConfirmationModal}
        onConfirm={updateUserStatusInactive}
        actionType={actionType}
      />
      <OkModal
        show={showOkModal}
        onClose={handleOkModalClose}
        onConfirm={handleOkModalClose}
      />
    </Container>
  );
}

// Export the component as the default export
export default ListScreen;
